/**
 * @fileoverview Blue Text Box Arrow Component
 * @active Used in current Next.js App Router routes
 *
 * This component renders a blue box with an arrow shape containing title text.
 * It's commonly used for section headers across the application.
 *
 * @see {@link src/components/ui/box/BlockWithBlueArrowTitle.tsx} - Parent component that uses this
 * @see {@link src/types/box.ts} - Type definitions for box components
 */

import { Flex, Heading } from "@chakra-ui/react";
import React from "react";

import { BlueTextBoxArrowProps } from "@/types/box";
import { h_700_16_100_32 } from "@/styles/fontStyles";

/**
 * A blue box with an arrow shape containing title text
 *
 * @component
 * @param {BlueTextBoxArrowProps} props - Component props
 * @returns {React.ReactElement} BlueTextBoxArrow component
 */
export const BlueTextBoxArrow = ({
  title,
  heading = "h4",
  headingStyles = {},
  boxStyles = {},
}: BlueTextBoxArrowProps): React.ReactElement => {
  return (
    <Flex
      maxW={"fit-content"}
      h={"30px"}
      color={"dashboardBackground"}
      bgColor={"mainBlue"}
      alignItems={"center"}
      justifyContent={"center"}
      pt={"0px"}
      pb={"0px"}
      px={"20px"}
      position={"relative"}
      clipPath={"polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"}
      {...boxStyles}
    >
      <Heading 
        as={heading} 
        {...h_700_16_100_32} 
        mr={"14px"} 
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        height={"100%"}
        {...headingStyles}
      >
        {title}
      </Heading>
    </Flex>
  );
};
